import React from 'react'

/**
 * Ilustración original para el panel del login: un amanecer sobre el pueblo y, en
 * primer plano, una comunidad que se cuida — una mamá con su bebé, un papá que lleva
 * a su hijo de la mano, un abuelo con su bastón — y un corazón sobre ellos.
 *
 * Es SVG en línea a propósito: no hay archivo que pueda dar 403, no hay que esperar
 * una descarga y pesa unos pocos KB. El cielo es transparente: el degradado verde del
 * panel queda de fondo y la luz del sol se mezcla con él.
 */
export default function IlustracionAccionSocial({ className }) {
  return (
    <svg viewBox="0 186 800 334" preserveAspectRatio="xMidYMax slice" className={className} role="img"
      aria-label="Una familia y un abuelo caminando juntos bajo un corazón, al amanecer sobre el pueblo">
      <defs>
        <radialGradient id="sr-sol" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#ffe9b0" stopOpacity="0.95" />
          <stop offset="0.35" stopColor="#f7c35f" stopOpacity="0.45" />
          <stop offset="1" stopColor="#f7c35f" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sr-corazon" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#ff8fa3" stopOpacity="0.55" />
          <stop offset="1" stopColor="#ff8fa3" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="sr-desvanece" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#000" />
          <stop offset="1" stopColor="#fff" />
        </linearGradient>
        <mask id="sr-mascara" maskUnits="userSpaceOnUse" x="0" y="186" width="800" height="150">
          <rect x="0" y="186" width="800" height="150" fill="url(#sr-desvanece)" />
        </mask>
        <linearGradient id="sr-colina" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2f8f4e" />
          <stop offset="1" stopColor="#14492a" />
        </linearGradient>
      </defs>

      {/* Luz del amanecer */}
      <circle cx="590" cy="318" r="250" fill="url(#sr-sol)" mask="url(#sr-mascara)" />
      <circle cx="590" cy="318" r="52" fill="#ffe7a8" />

      {/* Pájaros */}
      <g fill="none" stroke="#ffffff" strokeOpacity="0.7" strokeWidth="2.4" strokeLinecap="round">
        <path d="M650 224 q9 -9 18 0 q9 -9 18 0" />
        <path d="M700 250 q7 -7 14 0 q7 -7 14 0" />
        <path d="M600 244 q6 -6 12 0 q6 -6 12 0" />
      </g>

      {/* Colinas lejanas y el pueblo */}
      <path d="M0 372 Q140 318 290 352 T580 344 T800 330 V520 H0Z" fill="#2b7a43" opacity="0.6" />
      <g>
        {[
          [610, 348, '#f6e7c8', '#e07a5f'],
          [652, 340, '#fbeed2', '#d9694a'],
          [698, 346, '#f6e7c8', '#e9b44c'],
          [742, 338, '#fbeed2', '#e07a5f'],
        ].map(([x, y, pared, techo], i) => (
          <g key={i}>
            <rect x={x} y={y} width="34" height="26" rx="2" fill={pared} />
            <path d={`M${x - 5} ${y} L${x + 17} ${y - 19} L${x + 39} ${y} Z`} fill={techo} />
            <rect x={x + 6} y={y + 7} width="7" height="8" rx="1" fill="#ffd166" />
            <rect x={x + 21} y={y + 12} width="8" height="14" rx="1" fill="#8d5a3b" />
          </g>
        ))}
      </g>
      <path d="M0 410 Q210 356 410 398 T800 380 V520 H0Z" fill="#1f6a37" />

      {/* Árbol */}
      <rect x="86" y="388" width="11" height="58" rx="3" fill="#6b4426" />
      <circle cx="92" cy="366" r="36" fill="#2f9e4f" />
      <circle cx="66" cy="386" r="24" fill="#3fb561" />
      <circle cx="120" cy="386" r="26" fill="#3fb561" />
      <circle cx="98" cy="350" r="18" fill="#52c46f" />

      {/* Colina del primer plano */}
      <path d="M0 452 Q250 414 480 442 T800 432 V520 H0Z" fill="url(#sr-colina)" />
      <g stroke="#52c46f" strokeWidth="2.2" strokeLinecap="round" opacity="0.8">
        <path d="M60 470 l-3 -10 M66 470 l2 -11 M72 470 l5 -8" />
        <path d="M640 466 l-3 -10 M646 466 l2 -11 M652 466 l5 -8" />
        <path d="M205 480 l-3 -9 M211 480 l2 -10" />
      </g>

      {/* Corazón sobre la comunidad */}
      <circle cx="400" cy="232" r="64" fill="url(#sr-corazon)" />
      <path d="M400 266 C348 228 358 194 384 194 C395 194 400 202 400 209 C400 202 405 194 416 194 C442 194 452 228 400 266Z" fill="#ff7a8a" />
      <path d="M372 210 C366 214 364 222 368 230" fill="none" stroke="#ffffff" strokeOpacity="0.55" strokeWidth="4" strokeLinecap="round" />
      <g fill="#ff9aa9">
        <path d="M322 250 c-9 -7 -7 -14 -1 -14 c3 0 4 3 4 4 c0 -1 1 -4 4 -4 c6 0 8 7 -1 14 l-3 2z" />
        <path d="M472 236 c-7 -5 -5 -11 -1 -11 c2 0 3 2 3 3 c0 -1 1 -3 3 -3 c4 0 6 6 -1 11 l-2 2z" />
      </g>
      <g fill="#ffffff" opacity="0.85">
        <circle cx="318" cy="198" r="2.2" /><circle cx="486" cy="206" r="2" /><circle cx="360" cy="168" r="1.8" />
        <circle cx="440" cy="172" r="2.2" /><circle cx="510" cy="270" r="1.8" />
      </g>

      {/* Mamá con su bebé */}
      <g>
        <rect x="262" y="420" width="9" height="30" rx="3" fill="#23402f" />
        <rect x="277" y="420" width="9" height="30" rx="3" fill="#23402f" />
        <path d="M248 360 Q273 347 298 360 L306 424 H240 Z" fill="#ff9e80" />
        <rect x="267" y="338" width="12" height="12" rx="4" fill="#a8704a" />
        <circle cx="273" cy="330" r="17" fill="#b87b52" />
        <path d="M256 328 Q257 308 273 308 Q290 308 290 328 Q283 318 273 318 Q263 318 256 328Z" fill="#1d1410" />
        <circle cx="273" cy="304" r="8" fill="#1d1410" />
        <path d="M253 368 Q262 396 288 386" fill="none" stroke="#b87b52" strokeWidth="8" strokeLinecap="round" />
        <circle cx="293" cy="378" r="13" fill="#fff3d6" />
        <circle cx="294" cy="367" r="8.5" fill="#f0c39a" />
        <path d="M287 365 Q294 358 301 365" fill="none" stroke="#3a281c" strokeWidth="2.6" strokeLinecap="round" />
      </g>

      {/* Hijo de la mano del papá */}
      <g>
        <rect x="330" y="428" width="7" height="22" rx="3" fill="#23402f" />
        <rect x="341" y="428" width="7" height="22" rx="3" fill="#23402f" />
        <path d="M322 404 Q337 395 352 404 L354 430 H320 Z" fill="#ffd166" />
        <circle cx="337" cy="390" r="12.5" fill="#f0c39a" />
        <path d="M324 388 Q325 374 337 374 Q350 374 350 388 Q344 380 337 380 Q330 380 324 388Z" fill="#5a3a22" />
        <path d="M349 408 Q353 402 355 398" fill="none" stroke="#f0c39a" strokeWidth="6" strokeLinecap="round" />
      </g>

      {/* Papá */}
      <g>
        <rect x="389" y="414" width="10" height="36" rx="3" fill="#23402f" />
        <rect x="404" y="414" width="10" height="36" rx="3" fill="#23402f" />
        <path d="M377 356 Q402 343 427 356 L433 420 H371 Z" fill="#b6ffb8" />
        <rect x="396" y="338" width="12" height="12" rx="4" fill="#e9b98f" />
        <circle cx="402" cy="328" r="17.5" fill="#f0c39a" />
        <path d="M385 326 Q386 306 402 306 Q419 306 419 326 Q412 316 402 316 Q392 316 385 326Z" fill="#3a281c" />
        <path d="M380 364 Q363 384 355 399" fill="none" stroke="#f0c39a" strokeWidth="8" strokeLinecap="round" />
        <path d="M424 364 Q444 384 455 396" fill="none" stroke="#f0c39a" strokeWidth="8" strokeLinecap="round" />
      </g>

      {/* Abuelo con bastón */}
      <g>
        <rect x="465" y="418" width="9" height="32" rx="3" fill="#3b4a42" />
        <rect x="479" y="418" width="9" height="32" rx="3" fill="#3b4a42" />
        <path d="M454 362 Q476 350 498 362 L503 424 H449 Z" fill="#fff3d6" />
        <rect x="470" y="342" width="12" height="12" rx="4" fill="#dca97f" />
        <circle cx="476" cy="332" r="15.5" fill="#e8b88f" />
        <path d="M461 331 Q462 316 476 316 Q491 316 491 331 Q485 323 476 323 Q467 323 461 331Z" fill="#eceff1" />
        <path d="M456 372 Q452 386 458 396" fill="none" stroke="#e8b88f" strokeWidth="8" strokeLinecap="round" />
        <path d="M497 380 Q505 384 506 396" fill="none" stroke="#e8b88f" strokeWidth="7" strokeLinecap="round" />
        <path d="M506 392 L511 450" fill="none" stroke="#c9a66b" strokeWidth="4.5" strokeLinecap="round" />
        <path d="M501 392 q5 -8 12 -2" fill="none" stroke="#c9a66b" strokeWidth="4.5" strokeLinecap="round" />
      </g>
    </svg>
  )
}
